/**
 * Catalogue + sponsors endpoint surface (docs/modules/prizes.md, docs/modules/sponsors.md). Thin wrappers over
 * the console client; shapes come from @windrop/contracts.
 */
import type {
  ApproveBrandRequest,
  ApproveFeedItemRequest,
  AssetDto,
  BrandDto,
  BrandListQuery,
  FinalizeAssetRequest,
  GenerateVariantMatrixRequest,
  LinkSponsorViewerRequest,
  LotterySponsorshipDto,
  PresignAssetRequest,
  PresignAssetResponse,
  PrizeAdminDto,
  PrizeDetailDto,
  PrizeListQuery,
  PrizeStockDto,
  ProductAdminDto,
  ProductFeedItemDto,
  ProductListQuery,
  SamplingRuleAdminDtoExt,
  SponsorAdminDto,
  SponsorListQuery,
  StockAdjustRequest,
  StockReconciliationDto,
  UpsertLotterySponsorshipRequestExt,
  UpsertBrandRequest,
  UpsertPrizeRequest,
  UpsertProductFeedItemRequest,
  UpsertProductRequest,
  UpsertSamplingRuleRequest,
  UpsertSponsorRequest,
  VariantMatrixDto,
} from '@windrop/contracts';
import { api, type QueryValue } from '../../lib/api/client';

const q = (o: object): Record<string, QueryValue> => o as Record<string, QueryValue>;

export const catalogApi = {
  prizes: {
    list: (query: PrizeListQuery = {}) => api.get<PrizeAdminDto[]>('/v1/admin/prizes', q(query)),
    get: (prizeId: string) => api.get<PrizeDetailDto>(`/v1/admin/prizes/${prizeId}`),
    create: (body: UpsertPrizeRequest) => api.post<PrizeDetailDto>('/v1/admin/prizes', body),
    update: (prizeId: string, body: Partial<UpsertPrizeRequest>) =>
      api.patch<PrizeDetailDto>(`/v1/admin/prizes/${prizeId}`, body),
    stock: (prizeId: string) => api.get<PrizeStockDto>(`/v1/admin/prizes/${prizeId}/stock`),
    adjustStock: (prizeId: string, body: StockAdjustRequest) =>
      api.post<PrizeDetailDto>(`/v1/admin/prizes/${prizeId}/stock`, body),
    reconciliation: (lotteryId: string) =>
      api.get<StockReconciliationDto>('/v1/admin/prizes/reconciliation', { lotteryId }),
  },
  products: {
    list: (query: ProductListQuery = {}) => api.get<ProductAdminDto[]>('/v1/admin/products', q(query)),
    get: (productId: string) => api.get<ProductAdminDto>(`/v1/admin/products/${productId}`),
    create: (body: UpsertProductRequest) => api.post<ProductAdminDto>('/v1/admin/products', body),
    update: (productId: string, body: UpsertProductRequest) =>
      api.patch<ProductAdminDto>(`/v1/admin/products/${productId}`, body, { method: 'PUT' } as never),
    generateVariants: (body: GenerateVariantMatrixRequest) =>
      api.post<VariantMatrixDto>('/v1/admin/products/generate-variants', body, { idempotencyKey: false }),
  },
  brands: {
    list: (query: BrandListQuery = {}) => api.get<BrandDto[]>('/v1/admin/brands', q(query)),
    create: (body: UpsertBrandRequest) => api.post<BrandDto>('/v1/admin/brands', body),
    update: (brandId: string, body: UpsertBrandRequest) =>
      api.patch<BrandDto>(`/v1/admin/brands/${brandId}`, body),
    approve: (brandId: string, body: ApproveBrandRequest) =>
      api.post<BrandDto>(`/v1/admin/brands/${brandId}/approve`, body),
  },
  feeds: {
    list: (brandId?: string) => api.get<ProductFeedItemDto[]>('/v1/admin/product-feeds', { brandId }),
    create: (body: UpsertProductFeedItemRequest) =>
      api.post<ProductFeedItemDto>('/v1/admin/product-feeds', body),
    approve: (feedItemId: string, body: ApproveFeedItemRequest) =>
      api.post<ProductFeedItemDto>(`/v1/admin/product-feeds/${feedItemId}/approve`, body),
  },
  assets: {
    presign: (body: PresignAssetRequest) => api.post<PresignAssetResponse>('/v1/admin/assets/presign', body),
    finalize: (assetId: string, body: FinalizeAssetRequest) =>
      api.post<AssetDto>(`/v1/admin/assets/${assetId}/finalize`, body),
  },
  sponsors: {
    list: (query: SponsorListQuery = {}) => api.get<SponsorAdminDto[]>('/v1/admin/sponsors', q(query)),
    get: (sponsorId: string) => api.get<SponsorAdminDto>(`/v1/admin/sponsors/${sponsorId}`),
    create: (body: UpsertSponsorRequest) => api.post<SponsorAdminDto>('/v1/admin/sponsors', body),
    update: (sponsorId: string, body: UpsertSponsorRequest) =>
      api.patch<SponsorAdminDto>(`/v1/admin/sponsors/${sponsorId}`, body),
    linkViewer: (sponsorId: string, body: LinkSponsorViewerRequest) =>
      api.post<SponsorAdminDto>(`/v1/admin/sponsors/${sponsorId}/viewers`, body),
  },
  sponsorships: {
    list: (lotteryId?: string) => api.get<LotterySponsorshipDto[]>('/v1/admin/sponsorships', { lotteryId }),
    upsert: (body: UpsertLotterySponsorshipRequestExt) =>
      api.patch<LotterySponsorshipDto>('/v1/admin/sponsorships', body, { method: 'PUT' } as never),
  },
  samplingRules: {
    list: (lotteryId: string) =>
      api.get<SamplingRuleAdminDtoExt[]>('/v1/admin/sampling-rules', { lotteryId }),
    create: (body: UpsertSamplingRuleRequest) =>
      api.post<SamplingRuleAdminDtoExt>('/v1/admin/sampling-rules', body),
    update: (ruleId: string, body: Partial<UpsertSamplingRuleRequest>) =>
      api.patch<SamplingRuleAdminDtoExt>(`/v1/admin/sampling-rules/${ruleId}`, body),
    remove: (ruleId: string) => api.delete<SamplingRuleAdminDtoExt>(`/v1/admin/sampling-rules/${ruleId}`),
  },
};

/**
 * §6.3 asset pipeline from the browser: presign → PUT bytes to the signed URL → finalize with alt text and the
 * rights confirmation. Returns the `asset:<id>` ref to store on products, prizes, brands or sponsors.
 */
export async function uploadAsset(
  file: File,
  purpose: PresignAssetRequest['purpose'],
  alt: string,
  fetchImpl: typeof fetch = fetch,
): Promise<AssetDto> {
  const presigned = await catalogApi.assets.presign({
    purpose,
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
  });
  const put = await fetchImpl(presigned.upload.url, {
    method: 'PUT',
    headers: presigned.upload.headers,
    body: file,
  });
  if (!put.ok) throw new Error(`upload failed (${put.status})`);
  return catalogApi.assets.finalize(presigned.assetId, { alt, rightsConfirmed: true });
}
